import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { 
  HealthResponse, 
  OptionFlowResponse, 
  ETFExposureResponse, 
  NewsResponse, 
  ConsolidatedEarningsResponse, 
  OptionHistoricResponse,
  WorkflowListResponse, 
  ColumnSelectionResponse, 
  AnalysisResponse,
  Institution,
  InstitutionActivity,
  InstitutionHolding,
  InstitutionSector,
  HottestChainsResponse
} from '../types';

class ApiService {
  private apiClient: AxiosInstance;

  constructor() {
    // Get API configuration from environment variables
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'https://marketintelligencelabs.com';
    const API_KEY = process.env.REACT_APP_API_KEY || '4JLKulmACBHAHfx8JDfY-vtq3jfdSaNuzEyBoQbrACl9t5e3CYtvUaZmIhwbD3i0IPbgIkvXZDjI9D3nqUcdfA';

    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: 500000,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
    });

    // Add request interceptor for logging and auth token
    this.apiClient.interceptors.request.use(
      async (config) => {
        console.log(`Making API request to: ${config.url}`);
        
        // Add Cognito auth token if available
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          // No auth token available, continue without it
          console.log('No auth token available for request');
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        // Enhanced error handling for authentication
        if (error.response?.status === 401) {
          console.error('Authentication failed - Invalid API key');
          error.message = 'Authentication failed. Please check your API key configuration.';
        } else if (error.response?.status === 403) {
          console.error('Access forbidden - Insufficient permissions');
          error.message = 'Access forbidden. You do not have permission to access this resource.';
        } else if (error.response?.status >= 500) {
          console.error('Server error:', error.response.status);
          error.message = 'Server error. Please try again later.';
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
          console.error('Network error:', error.message);
          error.message = 'Network error. Please check your internet connection.';
        }
        
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  async getHealth(): Promise<HealthResponse> {
    const response = await this.apiClient.get('/health');
    return response.data;
  }

  async getOptionFlow(params: {
    limit?: number;
    ticker?: string;
    min_premium?: number;
    option_type?: 'call' | 'put';
    expiry?: string;
  } = {}, useCache: boolean = true): Promise<OptionFlowResponse> {
    // Add use_cache parameter to request
    const requestParams = {
      ...params,
      use_cache: useCache
    };

    const response = await this.apiClient.get('/api/options/flow-alerts', { 
      params: requestParams 
    });
    
    console.log(`API response:`, response.data.data.length, 'alerts (cached:', response.data.from_cache || false, ')');
    return response.data;
  }

  async getTickerFlow(ticker: string, limit = 50, useCache: boolean = true): Promise<OptionFlowResponse> {
    const response = await this.apiClient.get(`/api/options/flow-alerts`, {
      params: { 
        ticker_symbol: ticker,
        limit,
        use_cache: useCache
      }
    });
    
    console.log(`Ticker flow for ${ticker}:`, response.data.data.length, 'alerts (cached:', response.data.from_cache || false, ')');
    return response.data;
  }

  async getHighPremiumTrades(minPremium = 10000, limit = 200, useCache: boolean = true): Promise<OptionFlowResponse> {
    const response = await this.apiClient.get('/api/options/flow-alerts', {
      params: { 
        min_premium: minPremium, 
        limit,
        use_cache: useCache
      }
    });
    
    console.log(`High premium trades (${minPremium}+):`, response.data.data.length, 'alerts (cached:', response.data.from_cache || false, ')');
    return response.data;
  }



  async getETFExposure(ticker: string): Promise<ETFExposureResponse> {
    const response = await this.apiClient.get(`/api/etfs/${ticker}/exposure`);
    return response.data;
  }

  // Cache management methods
  async clearCache(): Promise<void> {
    await this.apiClient.post('/api/options/cache/clear');
    console.log('Backend cache cleared');
  }

  async getCacheStats() {
    const response = await this.apiClient.get('/api/options/cache/stats');
    return response.data;
  }

  async getCacheHealth() {
    const response = await this.apiClient.get('/api/options/cache/health');
    return response.data;
  }

  async getNewsHeadlines(limit: number = 50): Promise<NewsResponse> {
    const response = await this.apiClient.get('/api/news/headlines', {
      params: { limit }
    });
    return response.data;
  }

  async getConsolidatedEarnings(params: {
    date?: string;
    ticker?: string;
    session_type?: 'premarket' | 'afterhours' | 'both';
  } = {}): Promise<ConsolidatedEarningsResponse> {
    const response = await this.apiClient.get('/api/earnings/consolidated', {
      params
    });
    return response.data;
  }

  async getOptionHistoricData(optionChainId: string, limit?: number): Promise<OptionHistoricResponse> {
    const params: { limit?: number } = {};
    if (limit) {
      params.limit = limit;
    }
    
    const response = await this.apiClient.get(`/api/options/${optionChainId}/historic`, {
      params
    });
    
    console.log(`Historic data for ${optionChainId}:`, response.data.chains.length, 'records');
    return response.data;
  }

  // Test API connection and authentication
  async testApiConnection(): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      // Test health endpoint (no auth required)
      const healthResponse = await this.apiClient.get('/health');
      console.log('✅ Health check passed:', healthResponse.data);
      
      // Test authenticated endpoint
      const newsResponse = await this.apiClient.get('/api/news/headlines?limit=1');
      console.log('✅ News API working:', newsResponse.data);
      
      return {
        success: true,
        message: 'API integration successful!',
        data: {
          health: healthResponse.data,
          news: newsResponse.data
        }
      };
    } catch (error: any) {
      console.error('❌ API integration failed:', error);
      return {
        success: false,
        message: error.message || 'API integration failed'
      };
    }
  }

  // Generic Analysis API methods
  async getAnalysisWorkflows(): Promise<WorkflowListResponse> {
    const response = await this.apiClient.get('/api/analysis/workflows');
    return response.data;
  }

  async getWorkflowColumns(workflowId: string): Promise<ColumnSelectionResponse> {
    const response = await this.apiClient.get(`/api/analysis/workflows/${workflowId}/columns`);
    return response.data;
  }

  async performAnalysis(data: {
    workflow_id: string;
    data: any[];
    custom_instructions?: string;
  }): Promise<AnalysisResponse> {
    const response = await this.apiClient.post('/api/analysis/analyze', data);
    return response.data;
  }

  // Top Movers AI Analysis
  async getTopMoversAnalysis(date?: string): Promise<{
    analysis: string;
    analysis_date?: string;
    top_movers: Array<{
      ticker: string;
      direction: string;
      total_premium: number;
      buy_pct: number;
      sell_pct: number;
      hottest_chain: string;
    }>;
    last_updated: string;
    next_update: string;
    flows_analyzed: number;
    tickers_analyzed: number;
  }> {
    const params = date ? { date } : {};
    const response = await this.apiClient.get('/api/analysis/top-movers', { params });
    console.log('Top movers analysis loaded:', response.data.top_movers?.length || 0, 'movers', 'for date:', date || 'today');
    return response.data;
  }

  async getTopMoversStatus(): Promise<{
    is_running: boolean;
    is_market_hours: boolean;
    current_time_est: string;
    analysis_interval_minutes: number;
  }> {
    const response = await this.apiClient.get('/api/analysis/top-movers/status');
    return response.data;
  }

  async refreshTopMoversAnalysis(): Promise<any> {
    const response = await this.apiClient.post('/api/analysis/top-movers/refresh');
    console.log('Top movers analysis refreshed manually');
    return response.data;
  }

  // Get API configuration info (for debugging)
  getApiConfig(): { baseURL: string; hasApiKey: boolean } {
    return {
      baseURL: this.apiClient.defaults.baseURL || '',
      hasApiKey: !!this.apiClient.defaults.headers['X-API-Key']
    };
  }

  // Institution API methods
  async getInstitutions(params: {
    limit?: number;
    page?: number;
    name?: string;
    tags?: string[];
    min_share_value?: number;
    max_share_value?: number;
    min_total_value?: number;
    max_total_value?: number;
    order?: string;
    order_direction?: 'asc' | 'desc';
  } = {}): Promise<Institution[]> {
    const response = await this.apiClient.get('/api/institutions', {
      params
    });
    console.log(`Institutions loaded:`, response.data.length, 'institutions');
    return response.data;
  }

  async getInstitutionActivity(name: string, params: {
    date?: string;
    limit?: number;
    page?: number;
  } = {}): Promise<InstitutionActivity[]> {
    const response = await this.apiClient.get(`/api/institutions/${encodeURIComponent(name)}/activity`, {
      params
    });
    console.log(`Activity for ${name}:`, response.data.length, 'records');
    return response.data;
  }

  async getInstitutionHoldings(name: string, params: {
    limit?: number;
    page?: number;
  } = {}): Promise<InstitutionHolding[]> {
    const response = await this.apiClient.get(`/api/institutions/${encodeURIComponent(name)}/holdings`, {
      params
    });
    console.log(`Holdings for ${name}:`, response.data.length, 'records');
    return response.data;
  }

  async getInstitutionSectors(name: string): Promise<InstitutionSector[]> {
    const response = await this.apiClient.get(`/api/institutions/${encodeURIComponent(name)}/sectors`);
    console.log(`Sectors for ${name}:`, response.data.length, 'sectors');
    return response.data;
  }

  // Hottest Chains API methods
  async getHottestChains(params: {
    date: string;
    limit?: number;
    min_premium?: number;
  }): Promise<HottestChainsResponse> {
    const requestParams = {
      date: params.date,
      limit: params.limit || 250,
      min_premium: params.min_premium || '1000000',
    };
    
    const response = await this.apiClient.get('/api/options/chains/hottest', {
      params: requestParams
    });
    console.log(`Hottest chains loaded for ${params.date}:`, response.data.count, 'chains');
    return response.data;
  }

  async getChainsByTicker(ticker: string, params: {
    date: string;
    limit?: number;
    min_premium?: string;
  }): Promise<HottestChainsResponse> {
    const requestParams = {
      date: params.date,
      limit: params.limit || 50,
      min_premium: params.min_premium || '1000000',
    };
    
    const response = await this.apiClient.get(`/api/options/chains/by-ticker/${encodeURIComponent(ticker)}`, {
      params: requestParams
    });
    console.log(`Chains for ${ticker} on ${params.date}:`, response.data.count, 'chains');
    return response.data;
  }

}

export const apiService = new ApiService();
